import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from '@/components/ui/button';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { IconChevronLeft, IconChevronRight, IconDownload, IconLoader4, IconZoomIn, IconZoomOut } from '@tabler/icons-react';
import { computeFitScale } from '../lib/fit-to-width';

// Configure PDF.js worker - use CDN with matching version from react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface AdPdfViewerProps {
  pdfUrl: string;
}

// Options for PDF.js
const pdfOptions = {
  cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`,
  useWasm: true,
};

// Proxy URL in development to bypass CORS
const getProxiedUrl = (url: string): string => {
  if (import.meta.env.DEV && url.includes('assets.argretailers.com')) {
    return url.replace('https://assets.argretailers.com', '/proxy-assets');
  }
  return url;
};

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;
// p-4 padding on both sides of the page wrapper.
const PAGE_PADDING_X = 32;

const renderKey = (page: number, scale: number) => `${page}@${scale}`;

export const AdPdfViewer = ({ pdfUrl }: AdPdfViewerProps) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null);

  // Pages drawn so far, keyed by page *and* scale: a page drawn at an old
  // scale doesn't count, so a zoom or refit needs no reset step.
  const [renderedPages, setRenderedPages] = useState<Set<string>>(new Set());

  // Fit-to-width zoom: open at the scale that fills the scroll container and
  // refit on resize, until the user zooms by hand.
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [pageNaturalWidth, setPageNaturalWidth] = useState<number | null>(null);
  const [autoFit, setAutoFit] = useState(true);
  const [manualScale, setManualScale] = useState<number | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width !== undefined) setContainerWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Null until both widths are known, so nothing renders at a guessed size.
  const fitScale =
    containerWidth > 0 && pageNaturalWidth
      ? computeFitScale(containerWidth - PAGE_PADDING_X, pageNaturalWidth)
      : null;
  const scale = autoFit ? fitScale : manualScale;

  // One loader that stays up until the current page is drawn at its final
  // scale, so the page never appears at one size and then jumps.
  const currentPageReady = scale !== null && renderedPages.has(renderKey(pageNumber, scale));
  const showLoader = !error && (scale === null || !currentPageReady);

  // Fetch PDF as Uint8Array (safer than ArrayBuffer for React Strict Mode)
  useEffect(() => {
    let cancelled = false;

    const fetchPdf = async () => {
      const fetchUrl = getProxiedUrl(pdfUrl);
      try {
        console.log('Fetching PDF from:', fetchUrl);
        const response = await fetch(fetchUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        // Convert to Uint8Array to avoid "Buffer is already detached" error
        const uint8Array = new Uint8Array(arrayBuffer);

        if (!cancelled) {
          setPdfData(uint8Array);
          console.log('PDF fetched successfully, size:', uint8Array.byteLength);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch PDF:', err);
          setError(`Failed to fetch PDF: ${err instanceof Error ? err.message : 'Unknown error'}`);
          setIsLoading(false);
        }
      }
    };
    fetchPdf();

    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setIsLoading(false);
    setError(null);
    setRenderedPages(new Set());
    setPageNaturalWidth(null);
    setAutoFit(true);
    setManualScale(null);
  }, []);

  // Track when a page finishes rendering, at the scale it was drawn at.
  const onPageRenderSuccess = useCallback((pageNum: number, renderedAt: number) => {
    setRenderedPages((prev) => {
      const key = renderKey(pageNum, renderedAt);
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  // Determine which pages to render (current + adjacent for pre-loading)
  const pagesToRender = useMemo(() => {
    if (!numPages) return [1];
    const pages = new Set<number>();
    // Always include current page
    pages.add(pageNumber);
    // Pre-render adjacent pages
    if (pageNumber > 1) pages.add(pageNumber - 1);
    if (pageNumber < numPages) pages.add(pageNumber + 1);
    // Also pre-render one more ahead if available
    if (pageNumber + 2 <= numPages) pages.add(pageNumber + 2);
    return Array.from(pages).sort((a, b) => a - b);
  }, [pageNumber, numPages]);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('PDF load error:', error);
    console.error('PDF URL attempted:', pdfUrl);
    setError(`Failed to load the ad: ${error.message}`);
    setIsLoading(false);
  }, [pdfUrl]);

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages || prev));
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= (numPages || 1)) {
      setPageNumber(page);
    }
  };

  // A manual zoom stops fit-to-width for the rest of this document.
  const zoomIn = () => {
    if (scale === null) return;
    setAutoFit(false);
    setManualScale(Math.min(scale + ZOOM_STEP, ZOOM_MAX));
  };

  const zoomOut = () => {
    if (scale === null) return;
    setAutoFit(false);
    setManualScale(Math.max(scale - ZOOM_STEP, ZOOM_MIN));
  };

  const handleDownload = () => {
    window.open(pdfUrl, '_blank');
  };

  // Memoize the file object to prevent "Buffer is already detached" error
  // This happens because React Strict Mode runs effects twice in development
  const file = useMemo(() => {
    if (!pdfData) return null;
    return { data: pdfData };
  }, [pdfData]);

  // Generate page numbers for pagination
  const renderPagination = () => {
    if (!numPages || numPages <= 1) return null;

    const pages = Array.from({ length: numPages }, (_, i) => i + 1);

    return (
      <div className="flex items-center gap-1">
        {pages.map((page) => (
          <Button
            key={page}
            variant={page === pageNumber ? 'default' : 'ghost'}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => goToPage(page)}
          >
            {page}
          </Button>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Controls Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-background sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={goToPrevPage}
            disabled={pageNumber <= 1 || isLoading}
          >
            <IconChevronLeft className="h-4 w-4" />
            <span className="sr-only sm:not-sr-only sm:ml-1">Prev</span>
          </Button>

          {renderPagination()}

          <Button
            variant="outline"
            size="sm"
            onClick={goToNextPage}
            disabled={pageNumber >= (numPages || 1) || isLoading}
          >
            <span className="sr-only sm:not-sr-only sm:mr-1">Next</span>
            <IconChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomOut}
            disabled={scale === null || scale <= ZOOM_MIN || isLoading}
            aria-label="Zoom out"
          >
            <IconZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground min-w-[4rem] text-center">
            {scale === null ? '' : `${Math.round(scale * 100)}%`}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomIn}
            disabled={scale === null || scale >= ZOOM_MAX || isLoading}
            aria-label="Zoom in"
          >
            <IconZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            className="ml-2"
          >
            <IconDownload className="h-4 w-4" />
            <span className="sr-only sm:not-sr-only sm:ml-1">Download</span>
          </Button>
        </div>
      </div>

      {/* PDF Display Area */}
      <div className="relative flex-1 min-h-0">
        <div ref={containerRef} className="absolute inset-0 overflow-auto bg-muted/30">
          <div className="inline-flex justify-center p-4 min-h-full min-w-full">
            {error ? (
              <div className="flex flex-col items-center justify-center text-center p-8">
                <p className="text-destructive mb-4">{error}</p>
                <Button variant="outline" onClick={() => window.location.reload()}>
                  Try Again
                </Button>
              </div>
            ) : file ? (
              <Document
                file={file}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                options={pdfOptions}
                loading={null}
              >
                <div className="relative">
                  {/* Render current page and pre-render adjacent pages */}
                  {pagesToRender.map((pageNum) => (
                    <div
                      key={pageNum}
                      className={pageNum === pageNumber ? 'block' : 'absolute left-[-9999px] top-0'}
                      aria-hidden={pageNum !== pageNumber}
                    >
                      {/* Until the fit is known, load pages without drawing them
                          (page 1 still reports its width), so each page is drawn
                          once, at its final scale. */}
                      <Page
                        pageNumber={pageNum}
                        scale={scale ?? 1}
                        renderMode={scale === null ? 'none' : 'canvas'}
                        renderTextLayer={scale !== null}
                        renderAnnotationLayer={scale !== null}
                        loading={null}
                        onLoadSuccess={
                          pageNum === 1
                            ? (page) => setPageNaturalWidth((prev) => prev ?? page.originalWidth)
                            : undefined
                        }
                        onRenderSuccess={() => {
                          if (scale !== null) onPageRenderSuccess(pageNum, scale);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </Document>
            ) : null}
          </div>
        </div>

        {showLoader && (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/80"
            role="status"
            aria-live="polite"
          >
            <IconLoader4 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-sm text-muted-foreground">{pdfData ? 'Rendering ad...' : 'Fetching ad...'}</p>
          </div>
        )}
      </div>

      {/* Page indicator footer: kept mounted while loading so the viewing
          area doesn't shrink when the page count arrives. */}
      {(showLoader || (numPages !== null && numPages > 1)) && (
        <div className="px-4 py-2 border-t bg-background text-center">
          <p className="text-sm text-muted-foreground">
            {showLoader || numPages === null ? ' ' : `Page ${pageNumber} of ${numPages}`}
          </p>
        </div>
      )}
    </div>
  );
};
